import { useState, useCallback, useRef } from 'react';
import { apiRequest } from '../../services/apiMiddleware';
import { API_GATEWAY_PROCESS_NAME } from '../../utils/constants';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AgentQueryResponse {
  answer: string;
  nodeId: string;
}

const MAX_HISTORY_TURNS = 6;
const API_TIMEOUT_MS = 30000;

export const useAgentChat = (nodeId: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const idCounter = useRef(0);
  const messagesRef = useRef<Message[]>([]);

  const generateId = () => {
    idCounter.current += 1;
    return `msg-${idCounter.current}-${Date.now()}`;
  };

  const buildHistory = (currentMessages: Message[]) => {
    const historyMessages = currentMessages.slice(-(MAX_HISTORY_TURNS * 2));
    return historyMessages.map(({ role, content }) => ({ role, content }));
  };

  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    const updatedMessages = [...messagesRef.current, userMessage];
    messagesRef.current = updatedMessages;
    setMessages(updatedMessages);
    setIsLoading(true);

    const history = buildHistory(updatedMessages);

    try {
      const response = await apiRequest<AgentQueryResponse>({
        resourcePath: '/rcc-agent/query',
        httpMethod: 'post',
        processName: API_GATEWAY_PROCESS_NAME,
        requestBody: {
          question: question.trim(),
          nodeId,
          history,
        },
        timeout: API_TIMEOUT_MS,
      });

      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: response.data?.answer || 'Sorry, I was unable to process your request.',
        timestamp: new Date(),
      };
      const withAssistant = [...messagesRef.current, assistantMessage];
      messagesRef.current = withAssistant;
      setMessages(withAssistant);
    } catch {
      const errorMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: 'Something went wrong. Please try again.',
        timestamp: new Date(),
      };
      const withError = [...messagesRef.current, errorMessage];
      messagesRef.current = withError;
      setMessages(withError);
    } finally {
      setIsLoading(false);
    }
  }, [nodeId, isLoading]);

  const clearMessages = useCallback(() => {
    messagesRef.current = [];
    setMessages([]);
  }, []);

  return { messages, isLoading, sendMessage, clearMessages };
};
